import React, { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import Counter from "./Counter";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

const InvestmentsBlock = ({ investments }) => {
  const [progress, setProgress] = useState(0);
  const [isSlideTransitioning, setIsSlideTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  const [plusVisible, setPlusVisible] = useState(false);

  const swiperRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    setProgress(0);
  }, [activeSlideIndex]);

  useEffect(() => {
    if (isSlideTransitioning || isPaused) return;

    const totalDuration = 8000;
    const updateInterval = 20;
    const step = 100 / (totalDuration / updateInterval);

    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(intervalRef.current);
          setTimeout(() => {
            if (swiperRef.current) {
              swiperRef.current.slideNext();
            }
          }, 300);
          return 100;
        }
        return prev + step;
      });
    }, updateInterval);

    return () => clearInterval(intervalRef.current);
  }, [isSlideTransitioning, isPaused, activeSlideIndex]);

  const handleSlideChangeStart = () => {
    setPlusVisible(false);
    setIsSlideTransitioning(true);
  };

  const handleSlideChangeEnd = (swiper) => {
    setIsSlideTransitioning(false);
    setProgress(0);
    setActiveSlideIndex(swiper.realIndex);
  };

  useEffect(() => {
    if (!isSlideTransitioning) {
      const timer = setTimeout(() => {
        setPlusVisible(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isSlideTransitioning, activeSlideIndex]);

  const togglePause = () => {
    setIsPaused((prevPaused) => {
      const newPausedState = !prevPaused;
      if (swiperRef.current) {
        if (newPausedState) {
          clearInterval(intervalRef.current);
          swiperRef.current.autoplay.stop();
        } else {
          swiperRef.current.autoplay.start();
        }
      }
      return newPausedState;
    });
  };

  return (
    <div className="investments-slider" onClick={togglePause}>
      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${progress}%` }}
        ></div>
      </div>

      <Swiper
        spaceBetween={30}
        slidesPerView={1}
        loop={true}
        speed={1200}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        modules={[EffectFade, Autoplay]}
        autoplay={{
          delay: 8000,
          disableOnInteraction: false,
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          if (isPaused) swiper.autoplay.stop();
        }}
        onSlideChangeTransitionStart={handleSlideChangeStart}
        onSlideChangeTransitionEnd={handleSlideChangeEnd}
      >
        {investments.map((investment, index) => (
          <SwiperSlide key={index}>
            <div className="investments-block fade-slide-in">
              <div className="content position-relative overflow-hidden">
                <div className="bg-overlay"></div>
                <img
                  src={investment.image.normal || "/default-image.jpg"}
                  srcSet={`${investment.image.normal} 1x, ${investment.image.retina} 2x`}
                  alt={investment.image.alt}
                  className="img-fluid bg-img"
                />

                <div className="container-fluid">
                  <div className="row">
                    <div className="col-xl-4 col-md-6 col-sm-8">
                      <div>
                        <span className="eyebrow">Félagið</span>
                        <img
                          src={investment.logo || "/default-logo.jpg"}
                          alt="Investment logo"
                          className="img-fluid logo"
                        />
                        <p className="p-body-2 description">
                          {investment.description}
                        </p>
                        <a
                          href="/fjarfestingar"
                          className="btn-default d-none d-lg-flex"
                        >
                          Allar fjárfestingar
                        </a>
                      </div>
                      <div className="statistic">
                        <span className="d-block font-suisse-thin number">
                          {activeSlideIndex === index ? (
                            <Counter
                              key={activeSlideIndex}
                              targetNumber={investment.statistic || 0}
                              slideDuration={8000}
                              isPaused={isPaused}
                              isSlideTransitioning={isSlideTransitioning}
                              resetTrigger={activeSlideIndex}
                            />
                          ) : (
                            "0"
                          )}
                          <span
                            className={`plus ${
                              plusVisible ? "plus-visible" : ""
                            }`}
                          >
                            +
                          </span>
                        </span>
                        <span className="font-suisse-thin text">
                          {investment.statistic_text}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default InvestmentsBlock;
